<template>
  <div id="energy-container">
    <LazyHH4 class="mt-5 mb-4">
      Efficacité énergétique
    </LazyHH4>

    <div class="row">
      <div class="col-12 col-md-6">
        <div
          v-if="performance"
          class="performance-block"
        >
          <p class="label">
            Diagnostic de performances énergétiques
          </p>
          <div class="d-flex">
            <div
              v-for="letter in grades"
              :key="letter"
              class="grade"
              :class="{
                [letter]: true,
                active: letter == getPerformanceGrade(performance),
              }"
            >
              {{ letter }}
            </div>
          </div>
          <div
            class="value-container"
            :style="{
              paddingLeft: getPaddingValue(getPerformanceGrade(performance)) + 'px',
            }"
          >
            <div class="value">
              {{ performance }}
            </div>
            <div class="unit">
              kWhEP/m².an
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-6">
        <div
          v-if="emission"
          class="emission-block"
        >
          <p class="label">
            Indice d'émission de gaz à effet de serre
          </p>
          <div class="d-flex">
            <div
              v-for="letter in grades"
              :key="letter"
              class="grade"
              :class="{
                [letter]: true,
                active: letter == getEmissionGrade(emission)
              }"
            >
              {{ letter }}
            </div>
          </div>
          <div
            class="value-container"
            :style="{
              paddingLeft: getPaddingValue(getEmissionGrade(emission)) + 'px',
            }"
          >
            <div class="value">
              {{ emission }}
            </div>
            <div class="unit">
              kgeqCO2/m².an
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!performance && !emission">
      Données non disponibles
    </div>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
  regulations?: Array<any>;
}>()

const grades = ['A', 'B', 'C', 'D', 'E', 'F', 'G']

const performance = computed(() => {
  let performance: any = null
  props.regulations?.forEach(function (regulation) {
    if (regulation.type === 'Énergie - Consommation conventionnelle') {
      performance = regulation
    }
  })
  return performance?.value
})

const emission = computed(() => {
  let emission: any = null
  props.regulations?.forEach(function (regulation) {
    if (regulation.type === 'Énergie - Estimation des émissions') {
      emission = regulation
    }
  })
  return emission?.value
})

function getPerformanceGrade (value: string) {
  const val = parseInt(value)
  if (val <= 70) {
    return 'A'
  } else if (val > 70 && val <= 110) {
    return 'B'
  } else if (val > 110 && val <= 180) {
    return 'C'
  } else if (val > 180 && val <= 250) {
    return 'D'
  } else if (val > 250 && val <= 330) {
    return 'E'
  } else if (val > 330 && val <= 420) {
    return 'F'
  } else {
    return 'G'
  }
}

function getEmissionGrade (value: string) {
  const val = parseInt(value)
  if (val <= 6) {
    return 'A'
  } else if (val > 6 && val <= 11) {
    return 'B'
  } else if (val > 11 && val <= 30) {
    return 'C'
  } else if (val > 30 && val <= 50) {
    return 'D'
  } else if (val > 50 && val <= 70) {
    return 'E'
  } else if (val > 70 && val <= 100) {
    return 'F'
  } else {
    return 'G'
  }
}

function getPaddingValue (grade: string) {
  const pos = grades.indexOf(grade)
  return pos * 35
}
</script>

<style lang="scss" scoped>
#energy-container {
  margin-bottom: 30px;
  color: $night-blue;
}

.label {
  font-weight: normal;
  margin-bottom: 1em;
}

.performance-block .grade,
.emission-block .grade {
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  overflow: visible;
  position: relative;
}

.performance-block {
  .A {
    background: #0ddb9d;
  }

  .B {
    background: #61f127;
  }

  .C {
    background: #cafa02;
  }

  .D {
    background: #f9dd17;
  }

  .E {
    background: #fdad00;
  }

  .F {
    background: #fc7130;
  }

  .G {
    background: #d94654;
  }
}

.emission-block {
  .A {
    background: #fdeafe;
  }

  .B {
    background: #ffdefd;
  }

  .C {
    background: #f9befd;
  }

  .D {
    background: #fb7eff;
  }

  .E {
    background: #f652fc;
  }

  .F {
    background: #e428f4;
  }

  .G {
    background: #b415b9;
  }
}

.grade.active {
  font-size: 110%;
  border-radius: 50%;
  border: 2px solid #fff;
  height: 45px;
  width: 45px;
  z-index: 999;
  margin: -5px;
}

.value-container .value {
  font-size: 22px;
  margin-bottom: -5px;
  font-weight: 600;
  padding-top: 5px;
  width: 38px;
  text-align: center;
}

.value-container .unit {
  font-size: 13px;
  font-weight: normal;
}
</style>
